import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CTAButton, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';

/**
 * Module 2 — A2.7 Recruitment pipeline · add/edit prospect form.
 *
 * The 9 agent-input fields from the spec
 * (TCN_Agent_App_Modules…docx "RECRUITMENT PIPELINE FIELDS (A2.7)"):
 *   creator_handle (tiktok_username, required) · tiktok_link (url) ·
 *   forecast_onboard_month (1st-of-month date) · status (funnel enum) ·
 *   source_from (source) · contacted (contacted_at, date) · replied (bool) ·
 *   last_follow_up_date (date) · support_needed (text).
 *
 * Props:
 *   - open: boolean
 *   - prospect: row to edit, or {} / null for a new prospect
 *   - onClose: () => void
 *   - onSave: (payload) => Promise — caller does the supabase upsert
 *   - onDelete?: (id) => Promise — shown only when editing an existing row
 */
const STATUSES = ['prospect', 'contacted', 'replied', 'negotiating', 'onboarded', 'declined'];

// "YYYY-MM-DD" → "YYYY-MM" for the <input type="month"> value.
const toMonthInput = (d) => (d ? String(d).slice(0, 7) : '');
// "YYYY-MM" → "YYYY-MM-01" for the date column.
const fromMonthInput = (m) => (m ? `${m}-01` : null);

const ProspectFormModal = ({ open, prospect, onClose, onSave, onDelete }) => {
  const { t } = useTranslation();
  const editing = Boolean(prospect?.id);

  const [handle, setHandle] = useState('');
  const [tiktokLink, setTiktokLink] = useState('');
  const [forecastMonth, setForecastMonth] = useState('');
  const [status, setStatus] = useState('prospect');
  const [source, setSource] = useState('');
  const [contactedAt, setContactedAt] = useState('');
  const [replied, setReplied] = useState(false);
  const [lastFollowUp, setLastFollowUp] = useState('');
  const [supportNeeded, setSupportNeeded] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed the form whenever the target prospect changes.
  useEffect(() => {
    if (!open) return;
    setHandle((prospect?.tiktok_username || '').replace(/^@/, ''));
    setTiktokLink(prospect?.tiktok_link || '');
    setForecastMonth(toMonthInput(prospect?.forecast_onboard_month));
    setStatus(prospect?.status || 'prospect');
    setSource(prospect?.source || '');
    setContactedAt(prospect?.contacted_at || '');
    setReplied(Boolean(prospect?.replied));
    setLastFollowUp(prospect?.last_follow_up_date || '');
    setSupportNeeded(prospect?.support_needed || '');
    setError(null);
  }, [open, prospect]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const cleanHandle = handle.trim().replace(/^@/, '');
    if (!cleanHandle) {
      setError(t('tcn.m2.pipeline.form.handleRequired', 'Creator handle is required'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSave?.({
        tiktok_username: cleanHandle,
        tiktok_link: tiktokLink.trim() || null,
        forecast_onboard_month: fromMonthInput(forecastMonth),
        status,
        source: source.trim() || null,
        contacted_at: contactedAt || null,
        replied,
        last_follow_up_date: lastFollowUp || null,
        support_needed: supportNeeded.trim() || null,
      });
      onClose?.();
    } catch (err) {
      setError(err?.message || t('tcn.m2.pipeline.form.error', 'Failed to save'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editing || !onDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete(prospect.id);
      onClose?.();
    } catch (err) {
      setError(err?.message || t('tcn.m2.pipeline.form.error', 'Failed to delete'));
    } finally {
      setDeleting(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 520,
          maxHeight: '92dvh',
          overflowY: 'auto',
          background: TCN.surface1,
          border: `1px solid ${TCN.borderStrong}`,
          borderRadius: '20px 20px 0 0',
          padding: 20,
          color: TCN.text,
          fontFamily: TCN.fontSans,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>
            {editing
              ? t('tcn.m2.pipeline.form.editTitle', 'Edit prospect')
              : t('tcn.m2.pipeline.form.addTitle', 'Add prospect')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('tcn.m2.pipeline.form.close', 'Close')}
            style={{
              background: 'transparent',
              border: 'none',
              color: TCN.textDim,
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field
            label={t('tcn.m2.pipeline.form.handle', 'Creator handle')}
            value={handle}
            onChange={setHandle}
            placeholder="aliyaa.dxb"
            prefix="@"
          />
          <Field
            label={t('tcn.m2.pipeline.form.tiktokLink', 'TikTok link')}
            value={tiktokLink}
            onChange={setTiktokLink}
            placeholder="https://tiktok.com/@aliyaa.dxb"
            type="url"
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <SelectField
              label={t('tcn.m2.pipeline.form.status', 'Status')}
              value={status}
              onChange={setStatus}
              options={STATUSES.map((s) => ({
                value: s,
                label: t(`tcn.m2.pipeline.status.${s}`, s.charAt(0).toUpperCase() + s.slice(1)),
              }))}
            />
            <Field
              label={t('tcn.m2.pipeline.form.forecastMonth', 'Forecast onboard')}
              value={forecastMonth}
              onChange={setForecastMonth}
              type="month"
            />
          </div>
          <Field
            label={t('tcn.m2.pipeline.form.source', 'Source from')}
            value={source}
            onChange={setSource}
            placeholder={t('tcn.m2.pipeline.form.sourcePh', 'Cold DM, Referral, TikTok search…')}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field
              label={t('tcn.m2.pipeline.form.contacted', 'Contacted')}
              value={contactedAt}
              onChange={setContactedAt}
              type="date"
            />
            <Field
              label={t('tcn.m2.pipeline.form.lastFollowUp', 'Last follow-up')}
              value={lastFollowUp}
              onChange={setLastFollowUp}
              type="date"
            />
          </div>
          {/* replied (bool) */}
          <button
            type="button"
            onClick={() => setReplied((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              background: TCN.surface2,
              border: `1px solid ${replied ? TCN.cyan : TCN.border}`,
              borderRadius: 10,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: 6,
                border: `1px solid ${replied ? TCN.cyan : TCN.borderStrong}`,
                background: replied ? TCN.cyan : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {replied && <Icon name="check" size={13} color="#04141a" />}
            </span>
            <span style={{ fontSize: 14, color: TCN.text }}>
              {t('tcn.m2.pipeline.form.replied', 'Prospect has replied')}
            </span>
          </button>
          <Field
            label={t('tcn.m2.pipeline.form.supportNeeded', 'Support needed')}
            value={supportNeeded}
            onChange={setSupportNeeded}
            placeholder={t(
              'tcn.m2.pipeline.form.supportPh',
              'Equipment, schedule fit, contract terms…'
            )}
            multiline
          />
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 8,
              background: `${TCN.red}1f`,
              border: `1px solid ${TCN.red}33`,
              color: TCN.red,
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
          {editing && onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              style={{
                padding: '0 14px',
                height: 44,
                borderRadius: 12,
                background: 'transparent',
                border: `1px solid ${TCN.red}55`,
                color: TCN.red,
                fontFamily: 'inherit',
                fontSize: 14,
                fontWeight: 600,
                cursor: deleting ? 'wait' : 'pointer',
                flexShrink: 0,
              }}
            >
              {t('tcn.m2.pipeline.form.delete', 'Delete')}
            </button>
          )}
          <CTAButton variant="secondary" onClick={onClose} type="button">
            {t('tcn.m2.pipeline.form.cancel', 'Cancel')}
          </CTAButton>
          <CTAButton variant="primary" type="submit" loading={submitting}>
            {t('tcn.m2.pipeline.form.save', 'Save')}
          </CTAButton>
        </div>
      </form>
    </div>,
    document.body
  );
};

const inputBase = {
  width: '100%',
  padding: '10px 12px',
  background: TCN.surface2,
  border: `1px solid ${TCN.border}`,
  borderRadius: 10,
  color: TCN.text,
  fontFamily: 'inherit',
  // FRM-001/TCH-001 — 16px : pas de zoom iOS au focus.
  fontSize: 16,
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: TCN.textDim,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 6,
};

const Field = ({ label, value, onChange, placeholder, type = 'text', multiline = false, prefix }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        style={{ ...inputBase, resize: 'vertical' }}
      />
    ) : prefix ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          background: TCN.surface2,
          border: `1px solid ${TCN.border}`,
          borderRadius: 10,
        }}
      >
        <span style={{ paddingInlineStart: 12, color: TCN.textDim, fontSize: 16 }}>{prefix}</span>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ ...inputBase, background: 'transparent', border: 'none', paddingInlineStart: 4 }}
        />
      </div>
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputBase}
      />
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} style={inputBase}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

export default ProspectFormModal;
